/**
 * Custom-calendar authoring for Campaign Settings (#177, ADR-0004 §1/§4).
 *
 * The headline "custom calendars" capability: a GM authors / forks / edits /
 * retires fictional calendars on the settings → Calendars tab. Calendars are
 * the #3 "definition" bucket of the three-bucket model — they persist to the
 * op log as `pack.define_calendar` / `pack.retire_calendar` meta-ops under the
 * reserved `__pack` predicate (the SAME machinery the homebrew trait/predicate
 * editor uses), folding into the registry overlay (`getHomebrewDefs().calendars`).
 *
 * The campaign's CHOICE of canonical calendar is a separate *setting* — a
 * `config.set canonical_calendar` op (the existing `viewCanonicalCalendar`
 * path). "Set primary" here dispatches that same op.
 *
 * State lives in the HOST (`Model.calendarEditor`): the `@dl/ui`
 * `calendarEditor` atom is stateless — it takes the working draft + mode +
 * pre-computed issues and emits edit intent through callbacks. This module
 * owns the state shape, the reducer (ported from the gallery's
 * `reduceGalleryCdp` `ce-*` arms), the bundled→working / working→definition
 * converters, and the list/editor view.
 */

import {
  CalendarDefinitionSchema,
  closeOpenCycleInstance,
  isCycleBag,
  isPluginCalendar,
  loadBundledCalendar,
  loadBundledCalendars,
  normalizeWeekday,
  resolveLocalized,
  type Calendar,
  type CalendarDefinition,
  type LeapRule,
} from '@dl/calendar';
import {
  mintVocabId,
  packDefineCalendar,
  packRetireCalendar,
  type CalendarDefinitionLike,
  type PackDefineCalendarPayload,
  type PackRetireCalendarPayload,
} from '@dl/shared';
import {
  button as uiButton,
  calendarEditor,
  emptyState,
  validateWorkingCalendar,
  workingToDefinition,
  type CalendarEditorMessages,
  type CalendarIssue,
  type CalendarListEntry,
  type CalendarListSource,
  type WorkingCalendar,
  type WorkingEra,
  type WorkingMonth,
} from '@dl/ui';
import {
  branch,
  derived,
  div,
  each,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { loadActiveCalendar } from '../entities/active-calendar.js';
import { pluginCalendarNoticeText } from '../entities/calendar-degrade-notice.js';
import { getHomebrewDefs } from '../entities/homebrew-defs.js';
import { t } from '../i18n.js';

// ─── state ──────────────────────────────────────────────────────────────

/**
 * The host-owned calendar-editor slice. `null` when the list is showing
 * (no calendar open in the section editor). The working draft's numeric
 * fields are strings so an input can be empty / mid-keystroke (matching the
 * `@dl/ui` `WorkingCalendar` contract).
 */
export interface CalendarEditorState {
  /** The in-progress draft (opaque `cal_` id carried through, never edited). */
  working: WorkingCalendar;
  /** `simple` is the 95% rename path; `advanced` exposes the full model. */
  mode: 'simple' | 'advanced';
  /**
   * `create` mints a fresh `cal_` id on save; `edit` revises an existing
   * homebrew definition (same id, last-writer-wins); `fork` seeds the draft
   * from a bundled/pack calendar with a NEW minted id (the original is
   * read-only, the fork lives in this campaign).
   */
  origin: 'create' | 'edit' | 'fork';
}

/** Edit messages the reducer handles (ported from the gallery's `ce-*`). */
export type CalendarEditorMsg =
  | { type: 'cal-new' }
  | { type: 'cal-edit'; id: string }
  | { type: 'cal-fork'; id: string }
  | { type: 'cal-cancel' }
  | { type: 'ce-name'; name: string }
  | { type: 'ce-month'; index: number; patch: Partial<WorkingMonth> }
  | { type: 'ce-add-month' }
  | { type: 'ce-remove-month'; index: number }
  | { type: 'ce-toggle-intercalary'; index: number }
  | { type: 'ce-leap-rule'; rule: LeapRule }
  | { type: 'ce-era'; index: number; patch: Partial<WorkingEra> }
  | { type: 'ce-add-era' }
  | { type: 'ce-remove-era'; index: number }
  | { type: 'ce-weekday'; index: number; name: string }
  | { type: 'ce-add-weekday' }
  | { type: 'ce-remove-weekday'; index: number }
  | { type: 'ce-phase'; phase: string }
  | { type: 'ce-period'; index: number; name: string }
  | { type: 'ce-add-period' }
  | { type: 'ce-remove-period'; index: number }
  | { type: 'ce-anchor-day'; day: string }
  | { type: 'ce-anchor-name'; name: string }
  | { type: 'ce-toggle-advanced' };

// ─── converters ─────────────────────────────────────────────────────────

/** A blank, structurally-valid working calendar for "New calendar". */
export function blankWorkingCalendar(): WorkingCalendar {
  return {
    id: mintVocabId('calendar'),
    name: '',
    months: [
      { name: t('calendar_settings.sample.month_1'), length: '30' },
      { name: t('calendar_settings.sample.month_2'), length: '30' },
      { name: t('calendar_settings.sample.month_3'), length: '30' },
    ],
    leapRule: { kind: 'none' },
    eras: [
      {
        id: 'era_1',
        name: t('calendar_settings.sample.era_name'),
        abbreviation: '',
        startDay: '',
        yearOne: '1',
        direction: 'forward',
      },
    ],
    anchorCanonicalDay: '0',
    anchorName: '',
  };
}

/**
 * Project a runtime `Calendar` into an editable `WorkingCalendar`. `newId`
 * lets a fork carry a freshly-minted `cal_` id while an edit keeps the
 * original id. Numeric fields widen to strings; the optional weekday cycle
 * and sub-day periods carry through.
 */
export function calendarToWorking(calendar: Calendar, newId: string): WorkingCalendar {
  const months: WorkingMonth[] = calendar.cycles.year.months.map((m) => {
    const wm: WorkingMonth = { name: m.name, length: String(m.length) };
    if (m.leap_length !== undefined) wm.leapLength = String(m.leap_length);
    if (m.intercalary === true) wm.intercalary = true;
    return wm;
  });
  const eras: WorkingEra[] = calendar.eras.map((e) => {
    const we: WorkingEra = {
      id: e.id,
      name: e.name,
      startDay: e.start_day === null ? '' : String(e.start_day),
      yearOne: String(e.year_one),
      direction: e.direction,
    };
    if (e.abbreviation !== undefined) we.abbreviation = e.abbreviation;
    return we;
  });
  const working: WorkingCalendar = {
    id: newId,
    name: resolveLocalized(calendar.label) || calendar.id,
    months,
    leapRule: calendar.leap_rule,
    eras,
    anchorCanonicalDay: String(calendar.anchor_canonical_day),
  };
  const week = calendar.cycles.week;
  if (week !== undefined) {
    working.weekdays = {
      labels: week.labels.map((w) => ({ name: normalizeWeekday(w).name })),
      phase: String(week.phase),
    };
  }
  if (calendar.anchor !== undefined) {
    working.anchorName = resolveLocalized(calendar.anchor.name);
  }
  return working;
}

/**
 * Resolve a calendar id to its runtime `Calendar` (registry overlay wins over
 * a bundled definition of the same id), or `null` if unresolvable. Reuses the
 * `active-calendar` loader (the registry+bundled resolution chokepoint) for
 * folded homebrew ids; bundled ids fall straight through.
 */
function loadCalendarById(id: string): Calendar | null {
  const registry = getHomebrewDefs().calendars;
  try {
    if (registry.some((c) => c.id === id)) return loadActiveCalendar(id, registry);
    return loadBundledCalendar(id);
  } catch {
    return null;
  }
}

// ─── list entries ───────────────────────────────────────────────────────

/**
 * Build the list-manager rows: bundled starter calendars + folded
 * `pack.define_calendar` homebrew definitions. A homebrew definition shadows
 * a bundled one of the same id. The `primary` flag marks the campaign's
 * `canonical_calendar` choice. Names are localized.
 */
export function calendarListEntries(canonicalId: string | undefined): CalendarListEntry[] {
  const byId = new Map<string, CalendarListEntry>();
  try {
    for (const cal of loadBundledCalendars()) {
      byId.set(cal.id, {
        id: cal.id,
        name: resolveLocalized(cal.label) || cal.id,
        summary: structureSummary(cal),
        source: 'bundled',
      });
    }
  } catch {
    // A malformed bundled pack must not blank the list.
  }
  for (const def of getHomebrewDefs().calendars) {
    // A plugin-mode calendar (#242) LOADS but cannot render through the
    // structured engine; its row shows the "not fully supported" notice
    // (the calendar-dependent degrade surface) instead of a cycle summary.
    if (isPluginCalendar(def)) {
      byId.set(def.id, {
        id: def.id,
        name: resolveLocalized(def.name) || def.id,
        summary: pluginCalendarNoticeText(),
        source: 'campaign',
      });
      continue;
    }
    const cal = loadCalendarById(def.id);
    byId.set(def.id, {
      id: def.id,
      name: resolveLocalized(def.name) || def.id,
      summary: cal !== null ? structureSummary(cal) : '',
      source: 'campaign',
    });
  }
  const entries = [...byId.values()];
  if (canonicalId !== undefined) {
    for (const e of entries) e.primary = e.id === canonicalId;
  }
  return entries.sort((a, b) => {
    // Primary floats to the top, then campaign-authored, then bundled.
    if ((a.primary === true) !== (b.primary === true)) return a.primary === true ? -1 : 1;
    if (a.source !== b.source) return a.source === 'campaign' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** A short "12 months · 7-day week · leap" structure caption. */
function structureSummary(cal: Calendar): string {
  const parts: string[] = [
    t('calendar_settings.summary.months', { count: cal.cycles.year.months.length }),
  ];
  if (cal.cycles.week !== undefined) {
    parts.push(t('calendar_settings.summary.week', { count: cal.cycles.week.labels.length }));
  }
  if (cal.leap_rule.kind !== 'none') {
    parts.push(t('calendar_settings.summary.leap'));
  }
  return parts.join(' · ');
}

// ─── reducer ────────────────────────────────────────────────────────────

function patchMonth(months: WorkingMonth[], index: number, patch: Partial<WorkingMonth>): WorkingMonth[] {
  return months.map((mo, i) => (i === index ? { ...mo, ...patch } : mo));
}
function patchEra(eras: WorkingEra[], index: number, patch: Partial<WorkingEra>): WorkingEra[] {
  return eras.map((e, i) => (i === index ? { ...e, ...patch } : e));
}

/** Apply a `ce-*` edit to a working draft (host-owned). Pure. The `cal-*`
 * lifecycle messages (new/edit/fork/cancel) + `ce-toggle-advanced` are handled
 * by the host (they touch the wider slice), so they leave the draft untouched. */
export function reduceWorkingCalendar(
  working: WorkingCalendar,
  msg: CalendarEditorMsg,
): WorkingCalendar {
  switch (msg.type) {
    case 'ce-name':
      return { ...working, name: msg.name };
    case 'ce-month':
      return { ...working, months: patchMonth(working.months, msg.index, msg.patch) };
    case 'ce-add-month':
      return {
        ...working,
        months: [...working.months, { name: t('calendar_settings.new_month'), length: '30' }],
      };
    case 'ce-remove-month':
      return { ...working, months: working.months.filter((_, i) => i !== msg.index) };
    case 'ce-toggle-intercalary':
      return {
        ...working,
        months: working.months.map((mo, i) =>
          i === msg.index ? { ...mo, intercalary: !(mo.intercalary === true) } : mo,
        ),
      };
    case 'ce-leap-rule':
      return { ...working, leapRule: msg.rule };
    case 'ce-era':
      return { ...working, eras: patchEra(working.eras, msg.index, msg.patch) };
    case 'ce-add-era':
      return {
        ...working,
        eras: [
          ...working.eras,
          {
            id: `era_${working.eras.length + 1}`,
            name: t('calendar_settings.new_era'),
            startDay: '0',
            yearOne: '1',
            direction: 'forward',
          },
        ],
      };
    case 'ce-remove-era':
      return { ...working, eras: working.eras.filter((_, i) => i !== msg.index) };
    case 'ce-weekday': {
      const wd = working.weekdays;
      if (wd === undefined) return working;
      return {
        ...working,
        weekdays: { ...wd, labels: wd.labels.map((w, i) => (i === msg.index ? { name: msg.name } : w)) },
      };
    }
    case 'ce-add-weekday':
      return {
        ...working,
        weekdays:
          working.weekdays === undefined
            ? { labels: [{ name: t('calendar_settings.new_weekday') }], phase: '0' }
            : {
                ...working.weekdays,
                labels: [...working.weekdays.labels, { name: t('calendar_settings.new_weekday') }],
              },
      };
    case 'ce-remove-weekday': {
      const wd = working.weekdays;
      if (wd === undefined) return working;
      return { ...working, weekdays: { ...wd, labels: wd.labels.filter((_, i) => i !== msg.index) } };
    }
    case 'ce-phase': {
      const wd = working.weekdays;
      if (wd === undefined) return working;
      return { ...working, weekdays: { ...wd, phase: msg.phase } };
    }
    case 'ce-period':
      return {
        ...working,
        subDayPeriods: (working.subDayPeriods ?? []).map((p, i) =>
          i === msg.index ? { name: msg.name } : p,
        ),
      };
    case 'ce-add-period':
      return {
        ...working,
        subDayPeriods: [...(working.subDayPeriods ?? []), { name: t('calendar_settings.new_period') }],
      };
    case 'ce-remove-period':
      return {
        ...working,
        subDayPeriods: (working.subDayPeriods ?? []).filter((_, i) => i !== msg.index),
      };
    case 'ce-anchor-day':
      return { ...working, anchorCanonicalDay: msg.day };
    case 'ce-anchor-name':
      return { ...working, anchorName: msg.name };
    case 'cal-new':
    case 'cal-edit':
    case 'cal-fork':
    case 'cal-cancel':
    case 'ce-toggle-advanced':
      return working;
  }
}

/**
 * Open a calendar in the section editor for `edit` (homebrew, same id) or
 * `fork` (bundled/pack, fresh minted id). Returns `null` when the id can't
 * be resolved — the caller leaves the list showing.
 */
export function openCalendarForEdit(id: string, source: CalendarListSource): CalendarEditorState | null {
  const calendar = loadCalendarById(id);
  if (calendar === null) return null;
  if (source === 'campaign') {
    return { working: calendarToWorking(calendar, id), mode: 'advanced', origin: 'edit' };
  }
  // Bundled / plugin → fork into a fresh campaign-owned definition (new id).
  return { working: calendarToWorking(calendar, mintVocabId('calendar')), mode: 'advanced', origin: 'fork' };
}

/** Build the `pack.define_calendar` payload from a working draft, or `null`
 * when the draft does not fold (blocking validation errors). */
export function defineCalendarPayload(working: WorkingCalendar): PackDefineCalendarPayload | null {
  const def = workingToDefinition(working);
  if (def === null) return null;
  // `CalendarDefinition.name` is a `LocalizedString`; a bare string satisfies
  // the envelope. The opaque `cal_` id is carried through verbatim.
  return packDefineCalendar(def as CalendarDefinitionLike);
}

/** Build the `pack.retire_calendar` payload for a homebrew calendar id. */
export function retireCalendarPayload(id: string): PackRetireCalendarPayload {
  return packRetireCalendar(id);
}

// ─── open cycles — close_at affordance (#232, §4.1) ───────────────────────

/**
 * A still-OPEN cycle instance on a campaign-authored calendar (§4.1): an
 * `open`-cycle segment with no `end_day` yet ("winter is coming"). The GM
 * `close_at` affordance fills the `end_day` via a `pack.define_calendar`
 * definition update (LWW) — never a fact.
 */
export interface OpenCycleHandle {
  /** The homebrew calendar definition id (`cal_…`). */
  readonly calendarId: string;
  /** The localized calendar name (for the affordance label). */
  readonly calendarName: string;
  /** The open cycle's GM-chosen name (e.g. "season"). */
  readonly cycleName: string;
  /** The instance's index within the cycle's `instances` array. */
  readonly instanceIndex: number;
  /** The optional instance name (e.g. "winter"). */
  readonly instanceName?: string;
  /** The instance's `start_day` — the close day must be >= this. */
  readonly startDay: number;
}

/**
 * Resolve a folded homebrew calendar definition (bag-form) by id, validated
 * through the `@dl/calendar` chokepoint. Bundled calendars are never
 * open-cycle bearing (they ship the legacy `{ year, week }` arm), so only
 * the campaign overlay is consulted. Returns `null` when the id is not a
 * folded definition or fails structural validation.
 */
function loadCalendarDefinitionById(id: string): CalendarDefinition | null {
  const def = getHomebrewDefs().calendars.find((c) => c.id === id);
  if (def === undefined) return null;
  const parsed = CalendarDefinitionSchema.safeParse(def);
  return parsed.success ? parsed.data : null;
}

/**
 * Enumerate the still-open cycle instances across all campaign-authored
 * calendars (definitions whose bag carries an `open` cycle with an unset
 * `end_day`). The settings panel renders a `close_at` button per handle.
 * Bundled calendars are skipped (legacy arm, no open cycles).
 */
export function openCycleHandles(): OpenCycleHandle[] {
  const handles: OpenCycleHandle[] = [];
  for (const raw of getHomebrewDefs().calendars) {
    const def = loadCalendarDefinitionById(raw.id);
    // A plugin calendar (#242) has no structured cycles — nothing to close.
    if (def === null || def.mode === 'plugin' || !isCycleBag(def.cycles)) continue;
    const calendarName = resolveLocalized(def.name) || def.id;
    for (const cycle of def.cycles) {
      if (cycle.structure.kind !== 'open') continue;
      cycle.structure.instances.forEach((inst, instanceIndex) => {
        if (inst.end_day !== undefined) return; // already closed
        const handle: OpenCycleHandle = {
          calendarId: def.id,
          calendarName,
          cycleName: cycle.name,
          instanceIndex,
          startDay: inst.start_day,
        };
        handles.push(inst.name !== undefined ? { ...handle, instanceName: inst.name } : handle);
      });
    }
  }
  return handles;
}

/**
 * Build the `pack.define_calendar` payload that CLOSES an open-cycle
 * instance at `closeDay` (the GM `close_at` action, §4.1). The close is an
 * LWW **definition update** — the same `cal_` id, re-emitted with the
 * instance's `end_day` filled — NOT a fact. Returns `null` when the
 * calendar / cycle / instance can't be resolved or `closeDay` precedes the
 * instance's `start_day` (a non-sensical span).
 */
export function closeOpenCyclePayload(
  handle: OpenCycleHandle,
  closeDay: number,
): PackDefineCalendarPayload | null {
  const def = loadCalendarDefinitionById(handle.calendarId);
  if (def === null) return null;
  const next = closeOpenCycleInstance(def, handle.cycleName, handle.instanceIndex, closeDay);
  if (next === null) return null;
  return packDefineCalendar(next as CalendarDefinitionLike);
}

// ─── messages catalog ───────────────────────────────────────────────────

/** Thread the client `t()` into the stateless atom's chrome catalog. */
function editorMessages(): Partial<CalendarEditorMessages> {
  return {
    listEyebrow: t('calendar_editor.list.eyebrow'),
    listTitle: t('calendar_editor.list.title'),
    browsePacks: t('calendar_editor.browse_packs'),
    newCalendar: t('calendar_editor.new_calendar'),
    edit: t('calendar_editor.edit'),
    fork: t('calendar_editor.fork'),
    inspect: t('calendar_editor.inspect'),
    primary: t('calendar_editor.primary'),
    bundled: t('calendar_editor.bundled'),
    pluginPreview: t('calendar_editor.plugin_preview'),
    singlePrimaryTitle: t('calendar_editor.single_primary.title'),
    singlePrimaryBody: t('calendar_editor.single_primary.body'),
    emptyTitle: t('calendar_editor.empty.title'),
    emptyBody: t('calendar_editor.empty.body'),
    backToSimple: t('calendar_editor.back_to_simple'),
    advanced: t('calendar_editor.advanced'),
    cancel: t('calendar_editor.cancel'),
    save: t('calendar_editor.save'),
    done: t('calendar_editor.done'),
    identity: t('calendar_editor.identity'),
    calendarName: t('calendar_editor.calendar_name'),
    months: t('calendar_editor.months'),
    monthsHint: t('calendar_editor.months_hint'),
    renameOnly: t('calendar_editor.rename_only'),
    addMonth: t('calendar_editor.add_month'),
    markIntercalary: t('calendar_editor.mark_intercalary'),
    remove: t('calendar_editor.remove'),
    festival: t('calendar_editor.festival'),
    leapRule: t('calendar_editor.leap_rule'),
    leapNone: t('calendar_editor.leap_none'),
    leapEveryN: t('calendar_editor.leap_every_n'),
    leapJulian: t('calendar_editor.leap_julian'),
    leapGregorian: t('calendar_editor.leap_gregorian'),
    leapEvery: t('calendar_editor.leap_every'),
    leapPhase: t('calendar_editor.leap_phase'),
    leapHint: t('calendar_editor.leap_hint'),
    eras: t('calendar_editor.eras'),
    erasHint: t('calendar_editor.eras_hint'),
    eraName: t('calendar_editor.era_name'),
    abbreviation: t('calendar_editor.abbreviation'),
    startDay: t('calendar_editor.start_day'),
    startDayHelp: t('calendar_editor.start_day_help'),
    yearOne: t('calendar_editor.year_one'),
    yearOneHelp: t('calendar_editor.year_one_help'),
    direction: t('calendar_editor.direction'),
    forward: t('calendar_editor.forward'),
    backward: t('calendar_editor.backward'),
    addEra: t('calendar_editor.add_era'),
    weekdayCycle: t('calendar_editor.weekday_cycle'),
    weekdayHint: t('calendar_editor.weekday_hint'),
    phase: t('calendar_editor.phase'),
    phaseHelp: t('calendar_editor.phase_help'),
    addWeekday: t('calendar_editor.add_weekday'),
    subDay: t('calendar_editor.sub_day'),
    subDayHint: t('calendar_editor.sub_day_hint'),
    addPeriod: t('calendar_editor.add_period'),
    period: t('calendar_editor.period'),
    anchor: t('calendar_editor.anchor'),
    anchorHint: t('calendar_editor.anchor_hint'),
    anchorDay: t('calendar_editor.anchor_day'),
    anchorEvent: t('calendar_editor.anchor_event'),
    livePreview: t('calendar_editor.live_preview'),
    updatesAsYouEdit: t('calendar_editor.updates_as_you_edit'),
    readsToPlayers: t('calendar_editor.reads_to_players'),
    optional: t('calendar_editor.optional'),
    required: t('calendar_editor.required'),
    lengthLocked: t('calendar_editor.length_locked'),
    days: t('calendar_editor.days'),
    errorsBlock: t('calendar_editor.errors_block'),
    noErrors: t('calendar_editor.no_errors'),
  };
}

function resolveIssue(issue: CalendarIssue): string {
  return t(issue.messageKey, issue.params);
}

// ─── view ───────────────────────────────────────────────────────────────

/**
 * The minimal Model slice the calendars panel reads: the editor draft/mode,
 * the folded config (for the canonical pointer), and `entities` (the
 * reactivity handle — it changes on every op-log rebuild, re-reading the
 * module-scope `getHomebrewDefs().calendars` overlay). Generic so the host's
 * wider `Model` satisfies it structurally.
 */
export interface CalendarSettingsSlice {
  calendarEditor: CalendarEditorState | null;
  config: {
    canonical_calendar?: string | undefined;
    /**
     * The campaign's in-world "now" (ADR-0005). The `close_at` affordance
     * (§4.1) closes an open cycle at this cursor's `day`; absent / null when
     * no in-world date is set (the affordance is then disabled).
     */
    session_cursor?: { day: number; calendar_id: string } | null | undefined;
  };
  entities: Record<string, unknown>;
  session: { mode: string };
}

/** Host callbacks the panel emits (the host owns op minting + dispatch). */
export interface CalendarSettingsHandlers<Msg> {
  onEditorMsg: (msg: CalendarEditorMsg) => Msg;
  /** Persist the working draft (`pack.define_calendar`). */
  onSave: () => Msg;
  /** Retire a homebrew calendar (`pack.retire_calendar`). */
  onRetire: (id: string) => Msg;
  /** Point `canonical_calendar` at this id (`config.set`). */
  onSetPrimary: (id: string) => Msg;
  /**
   * Close an open-cycle instance at the campaign's current in-world day
   * (the `close_at` affordance, §4.1). The host turns the payload into a
   * `pack.define_calendar` op (LWW) — never a fact. `closeDay` is the
   * session cursor's day at click time.
   */
  onCloseOpenCycle: (handle: OpenCycleHandle, closeDay: number) => Msg;
}

/**
 * The Calendars settings panel. Shows the section editor ABOVE the list when
 * a calendar is open (the list stays visible — list-first CRUD), else just
 * the list. Both surfaces remount on draft/config/entities change via keyed
 * `each` digests so the embedded live-preview picker tracks the form.
 */
export function viewCalendarSettings<Slice extends CalendarSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  h: CalendarSettingsHandlers<Msg>,
): Mountable {
  return div(
    {
      class: 'settings-tab-panel',
      'data-testid': 'settings-tab-calendars',
      id: 'settings-calendars-panel',
      role: 'tabpanel',
    },
    [
      // Section editor — present only when a calendar is open.
      branch(state.map((s) => (s.calendarEditor !== null ? 'open' : 'closed')), {
        open: () => [viewSectionEditor(state, send, h)],
        closed: () => [],
      }),
      // List manager — remounts on (canonical pointer | calendar set) change.
      viewListManager(state, send, h),
    ],
  );
}

/** Digest the working draft so every edit remounts the stateless editor +
 * its embedded preview picker (construction-time atom pattern). */
function editorKey(s: CalendarEditorState): string {
  const w = s.working;
  const months = w.months
    .map((m) => `${m.name}:${m.length}:${m.leapLength ?? ''}:${m.intercalary ? 'f' : ''}`)
    .join(',');
  const eras = w.eras
    .map((e) => `${e.name}:${e.abbreviation ?? ''}:${e.startDay}:${e.yearOne}:${e.direction}`)
    .join(',');
  const wd = (w.weekdays?.labels ?? []).map((x) => x.name).join('|') + ':' + (w.weekdays?.phase ?? '');
  const periods = (w.subDayPeriods ?? []).map((p) => p.name).join('|');
  return `${s.mode}#${w.id}#${w.name}#${months}#${w.leapRule.kind}#${eras}#${wd}#${periods}#${w.anchorCanonicalDay}#${w.anchorName ?? ''}`;
}

function viewSectionEditor<Slice extends CalendarSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  h: CalendarSettingsHandlers<Msg>,
): Mountable {
  const messages = editorMessages();
  const model = derived([state], (s) => s);
  return each(
    model.map((s) => (s.calendarEditor !== null ? [editorKey(s.calendarEditor)] : [])),
    {
      key: (k) => k,
      render: () => {
        const editor = model.peek().calendarEditor;
        if (editor === null) return [];
        const issues = validateWorkingCalendar(editor.working);
        return [
          calendarEditor({
            mode: editor.mode,
            working: editor.working,
            issues,
            locale: 'en',
            messages,
            resolveIssue,
            testid: 'settings-calendar-editor',
            onName: (name) => send(h.onEditorMsg({ type: 'ce-name', name })),
            onMonth: (index, patch) => send(h.onEditorMsg({ type: 'ce-month', index, patch })),
            onAddMonth: () => send(h.onEditorMsg({ type: 'ce-add-month' })),
            onRemoveMonth: (index) => send(h.onEditorMsg({ type: 'ce-remove-month', index })),
            onToggleIntercalary: (index) => send(h.onEditorMsg({ type: 'ce-toggle-intercalary', index })),
            onLeapRule: (rule) => send(h.onEditorMsg({ type: 'ce-leap-rule', rule })),
            onEra: (index, patch) => send(h.onEditorMsg({ type: 'ce-era', index, patch })),
            onAddEra: () => send(h.onEditorMsg({ type: 'ce-add-era' })),
            onRemoveEra: (index) => send(h.onEditorMsg({ type: 'ce-remove-era', index })),
            onWeekday: (index, name) => send(h.onEditorMsg({ type: 'ce-weekday', index, name })),
            onAddWeekday: () => send(h.onEditorMsg({ type: 'ce-add-weekday' })),
            onRemoveWeekday: (index) => send(h.onEditorMsg({ type: 'ce-remove-weekday', index })),
            onPhase: (phase) => send(h.onEditorMsg({ type: 'ce-phase', phase })),
            onPeriod: (index, name) => send(h.onEditorMsg({ type: 'ce-period', index, name })),
            onAddPeriod: () => send(h.onEditorMsg({ type: 'ce-add-period' })),
            onRemovePeriod: (index) => send(h.onEditorMsg({ type: 'ce-remove-period', index })),
            onAnchorDay: (day) => send(h.onEditorMsg({ type: 'ce-anchor-day', day })),
            onAnchorName: (name) => send(h.onEditorMsg({ type: 'ce-anchor-name', name })),
            onToggleAdvanced: () => send(h.onEditorMsg({ type: 'ce-toggle-advanced' })),
            onCancel: () => send(h.onEditorMsg({ type: 'cal-cancel' })),
            onSave: () => send(h.onSave()),
          }),
        ];
      },
    },
  );
}

/** A digest of the list state so the manager remounts when the calendar set
 * or the canonical pointer changes. `entities` size is the rebuild handle.
 * The session-cursor day is folded in so the `close_at` affordance re-renders
 * (enabled/disabled) as the in-world cursor moves. */
function listKey(s: CalendarSettingsSlice): string {
  const defs = getHomebrewDefs().calendars.map((c) => c.id).join('|');
  const cursorDay = s.config.session_cursor?.day ?? '';
  return `${s.config.canonical_calendar ?? ''}#${defs}#${Object.keys(s.entities).length}#${cursorDay}`;
}

function viewListManager<Slice extends CalendarSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  h: CalendarSettingsHandlers<Msg>,
): Mountable {
  const messages = editorMessages();
  const model = derived([state], (s) => s);
  return each(model.map((s) => [listKey(s)]), {
    key: (k) => k,
    render: () => {
      const s = model.peek();
      const entries = calendarListEntries(s.config.canonical_calendar);
      return [
        calendarEditor({
          mode: 'list',
          entries,
          messages,
          testid: 'settings-calendar-manager',
          onOpenEntry: (id, source) =>
            send(h.onEditorMsg(source === 'campaign' ? { type: 'cal-edit', id } : { type: 'cal-fork', id })),
          onNewCalendar: () => send(h.onEditorMsg({ type: 'cal-new' })),
        }),
        // Per-calendar quick actions (set-primary / retire) the stateless list
        // atom doesn't render — a small action strip beneath the manager.
        ...(entries.length === 0
          ? [emptyState({ testid: 'settings-calendars-empty', message: text(t('calendar_settings.empty')) })]
          : entries.map((e) => viewActionStrip(e, send, h))),
        // Open-cycle `close_at` affordances (§4.1) — one per still-open
        // instance across campaign-authored calendars.
        ...viewOpenCycleStrips(s.config.session_cursor?.day ?? null, send, h),
      ];
    },
  });
}

/**
 * The `close_at` affordance strip (§4.1): one "close [instance] at day N"
 * button per still-open cycle instance. Closing emits a
 * `pack.define_calendar` op (LWW definition update) — never a fact. The
 * button is disabled when no in-world cursor is set (no day to close at).
 */
function viewOpenCycleStrips<Msg>(
  cursorDay: number | null,
  send: Send<Msg>,
  h: CalendarSettingsHandlers<Msg>,
): Mountable[] {
  const handles = openCycleHandles();
  if (handles.length === 0) return [];
  return handles.map((handle) => {
    const label = handle.instanceName ?? handle.cycleName;
    const canClose = cursorDay !== null && cursorDay >= handle.startDay;
    return div(
      {
        class: 'settings-calendar-actions',
        'data-testid': `settings-calendar-close-cycle-${handle.calendarId}-${handle.cycleName}-${handle.instanceIndex}`,
        role: 'group',
        'aria-label': t('calendar_settings.close_cycle_for', {
          name: handle.calendarName,
          cycle: label,
        }),
      },
      [
        uiButton({
          variant: 'secondary',
          testid: `settings-calendar-close-cycle-btn-${handle.calendarId}-${handle.cycleName}-${handle.instanceIndex}`,
          disabled: !canClose,
          onClick: () => {
            if (cursorDay === null) return;
            send(h.onCloseOpenCycle(handle, cursorDay));
          },
          children: [
            text(
              cursorDay === null
                ? t('calendar_settings.close_cycle_no_cursor', { cycle: label })
                : t('calendar_settings.close_cycle_at', { cycle: label, day: cursorDay }),
            ),
          ],
        }),
      ],
    );
  });
}

function viewActionStrip<Msg>(
  entry: CalendarListEntry,
  send: Send<Msg>,
  h: CalendarSettingsHandlers<Msg>,
): Mountable {
  const actions: Mountable[] = [];
  if (entry.primary !== true) {
    actions.push(
      uiButton({
        variant: 'secondary',
        testid: `settings-calendar-set-primary-${entry.id}`,
        onClick: () => send(h.onSetPrimary(entry.id)),
        children: [text(`${t('calendar_settings.set_primary')} — ${entry.name}`)],
      }),
    );
  }
  if (entry.source === 'campaign') {
    actions.push(
      uiButton({
        variant: 'danger',
        testid: `settings-calendar-retire-${entry.id}`,
        onClick: () => send(h.onRetire(entry.id)),
        children: [text(`${t('calendar_settings.retire')} — ${entry.name}`)],
      }),
    );
  }
  if (actions.length === 0) return div({ class: 'settings-calendar-actions' }, []);
  return div(
    {
      class: 'settings-calendar-actions',
      'data-testid': `settings-calendar-actions-${entry.id}`,
      role: 'group',
      'aria-label': t('calendar_settings.actions_for', { name: entry.name }),
    },
    actions,
  );
}
